<div class="dash">
  <!-- Whose outbox, which day, and the audience behind it -->
  <header class="dash__head">
    <div class="dash__intro">
      <p class="bk-eyebrow">{{ organizationName || "Dashboard" }}</p>
      <h1 class="dash__date">{{ today }}</h1>
      @if (statsPending) {
        <span class="bk-skel dash__audience-skel" aria-hidden="true"></span>
      } @else {
        <!-- Gaps, not dots, separate the figures, so a wrapped line never starts with a separator -->
        <ul class="dash__audience" aria-label="Audience">
          <li>
            <a routerLink="/organization/contacts">
              <strong>{{ formatCount(dashboardStats?.contactsCount) }}</strong>
              {{ plural(dashboardStats?.contactsCount ?? 0, "contact", "contacts") }}
            </a>
            in
            <a routerLink="/organization/contacts/organizations">
              <strong>{{ formatCount(organizationsCount) }}</strong>
              {{ plural(organizationsCount, "organization", "organizations") }}
            </a>
          </li>
          <li>
            <a routerLink="/organization/contacts/tags">
              <strong>{{ formatCount(tagsCount) }}</strong> {{ plural(tagsCount, "tag", "tags") }}
            </a>
          </li>
          <li>
            <a routerLink="/organization/contacts/segmented-list">
              <strong>{{ formatCount(segmentsCount) }}</strong>
              {{ plural(segmentsCount, "segment", "segments") }}
            </a>
          </li>
          <li>
            <strong>{{ formatCount(dashboardStats?.usersCount) }}</strong>
            {{ plural(dashboardStats?.usersCount ?? 0, "team member", "team members") }}
          </li>
        </ul>
      }
    </div>

    <div class="dash__actions">
      <button type="button" class="bk-btn bk-btn--ghost" (click)="goToCreateContacts()">
        <span class="bk-sym" aria-hidden="true">person_add</span>
        Add contacts
      </button>
      <button type="button" class="bk-btn bk-btn--ghost" (click)="goToCreateCampaigns()">
        <span class="bk-sym" aria-hidden="true">campaign</span>
        New campaign
      </button>
      <button type="button" class="bk-btn bk-btn--primary" (click)="goToCreateMessage()">
        <span class="bk-sym" aria-hidden="true">edit_square</span>
        Compose message
      </button>
    </div>
  </header>

  <!-- The last day and the next, either side of now -->
  <dash-outbox-line
    [now]="now"
    [recent]="dashboardMessages"
    [queue]="queue"
    [overdue]="overdue"
    [queueLoading]="!upcomingLoaded"
    (open)="openMessage($event)"
  />

  <!-- The page splits where the line's "now" falls: sent to the left, scheduled to the right -->
  <div class="split">
    <div class="split__col split__col--past">
      <p class="split__label split__label--past">
        <span class="bk-sym" aria-hidden="true">west</span>
        Sent
      </p>

      <!-- ── Last sent: the message itself, and whether it got through ── -->
      <section class="sec" aria-labelledby="dash-last">
        <header class="sec__head">
          <h2 class="sec__title" id="dash-last">Last sent</h2>
          @if (lastSent && isCompleted(lastSent)) {
            <button type="button" class="bk-more" (click)="openReport(lastSent)">
              View report <span class="bk-sym" aria-hidden="true">arrow_outward</span>
            </button>
          }
        </header>

        @if (lastSent) {
          <div class="last__top">
            <button type="button" class="last__meta" (click)="openMessage(lastSent)">
              <span class="last__subject">{{ lastSent.messageSubject || "Untitled message" }}</span>
              <span class="last__facts">
                <app-status [text]="lastSent.status" [status]="lastSent.status" />
                <span>{{ whenOf(lastSent) }}</span>
                @if (lastSent.from) {
                  <span>from {{ lastSent.from }}</span>
                }
              </span>
            </button>

            <!-- Did it get through? Answered here, without opening the report. -->
            @if (lastDelivery) {
              <div
                class="last__outcome"
                [class.has-failures]="lastDelivery.failed > 0"
                [attr.aria-label]="lastShare + ' delivered, ' + formatCount(lastDelivery.failed) + ' failed'"
              >
                <span class="last__share">{{ lastShare }}</span>
                <span class="last__share-label">{{ lastShareLabel }}</span>
                @if (lastDelivery.failed > 0) {
                  <span class="last__failed">{{ formatCount(lastDelivery.failed) }} failed</span>
                } @else {
                  <span class="last__of">of {{ formatCount(lastDelivery.total) }}</span>
                }
              </div>
            }
          </div>
          <bk-sms-bubble [text]="copyOf(lastSent)" variant="small" />
        } @else if (loading || lastSentPending) {
          <div class="skel-stack" aria-hidden="true">
            <span class="bk-skel" style="width: 46%; height: 20px"></span>
            <span class="bk-skel" style="width: 58%; height: 12px"></span>
            <span class="bk-skel skel-bubble"></span>
          </div>
        } @else {
          <div class="empty">
            <p class="empty__title">Nothing sent yet.</p>
            <p class="empty__body">
              Your most recent message shows here as soon as it goes out, with
              how many people it reached.
            </p>
            <button type="button" class="bk-btn bk-btn--primary" (click)="goToCreateMessage()">
              Compose message
            </button>
          </div>
        }
      </section>

      <!-- ── Recent messages ── -->
      <section class="sec" aria-labelledby="dash-recent">
        <header class="sec__head">
          <h2 class="sec__title" id="dash-recent">Recent messages</h2>
          <a class="bk-more" routerLink="/organization/messages">
            All messages <span class="bk-sym" aria-hidden="true">arrow_outward</span>
          </a>
        </header>

        @if (dashboardMessages.length) {
          <table class="bk-log recent">
            <thead class="bk-sr-only">
              <tr>
                <th scope="col">Message</th>
                <th scope="col">Status</th>
                <th scope="col">When</th>
              </tr>
            </thead>
            <tbody>
              @for (message of dashboardMessages; track message.id) {
                <tr class="is-link" (click)="openMessage(message)">
                  <td class="col-msg">
                    <span class="bk-msg">
                      <a
                        class="bk-msg__subject"
                        [routerLink]="['/organization/messages', message.uuid]"
                        (click)="selectFromLink($event, message)"
                      >{{ message.messageSubject || "Untitled message" }}</a>
                      <span class="bk-msg__preview">
                        <bk-sms-text [text]="copyOf(message)" [inline]="true" />
                      </span>
                    </span>
                  </td>
                  <td class="col-status">
                    <!-- Once sent, the outcome says more than "Completed" -->
                    @if (isSent(message)) {
                      <bk-delivery
                        [uuid]="message.uuid"
                        [status]="message.status"
                        [sentAt]="message.sendAt || message.createdAt"
                      />
                    } @else {
                      <app-status [text]="message.status" [status]="message.status" />
                    }
                  </td>
                  <td class="num quiet col-when">{{ whenOf(message) }}</td>
                </tr>
              }
            </tbody>
          </table>
        } @else if (loading) {
          <div class="skel-rows" aria-hidden="true">
            @for (row of [1, 2, 3, 4]; track row) {
              <span class="bk-skel" style="height: 14px"></span>
            }
          </div>
        } @else {
          <div class="empty">
            <p class="empty__title">No messages yet.</p>
            <p class="empty__body">Messages you compose or schedule are listed here.</p>
          </div>
        }
      </section>
    </div>

    <div class="split__col split__col--next">
      <p class="split__label split__label--next">
        Scheduled
        <span class="bk-sym" aria-hidden="true">east</span>
      </p>

      <!-- ── Up next: the queue, soonest first ── -->
      <section class="sec" aria-labelledby="dash-next">
        <header class="sec__head">
          <h2 class="sec__title" id="dash-next">
            Up next
            @if (upcomingLoaded && scheduledCount > 0) {
              <span class="sec__count">{{ formatCount(scheduledCount) }} scheduled</span>
            }
          </h2>
          @if (queue.length) {
            <a class="bk-more" routerLink="/organization/messages" [queryParams]="{ status: 'pending' }">
              All scheduled <span class="bk-sym" aria-hidden="true">arrow_outward</span>
            </a>
          }
        </header>

        @if (nextUp) {
          <button type="button" class="next__lead" (click)="openMessage(nextUp)">
            <span class="next__countdown">{{ countdownOf(nextUp) }}</span>
            <span class="next__subject">{{ nextUp.messageSubject || "Untitled message" }}</span>
            <span class="next__when">
              {{ whenOf(nextUp) }}
              @if (nextUp.from) {
                <span class="next__from">from {{ nextUp.from }}</span>
              }
            </span>
          </button>

          @if (laterInQueue.length) {
            <ol class="next__list" aria-label="After that">
              @for (message of laterInQueue; track message.uuid) {
                <li>
                  <button type="button" class="next__row" (click)="openMessage(message)">
                    <span class="next__row-when">{{ whenOf(message) }}</span>
                    <span class="next__row-subject">{{ message.messageSubject || "Untitled message" }}</span>
                    <span class="bk-sym next__row-go" aria-hidden="true">chevron_right</span>
                  </button>
                </li>
              }
            </ol>
          }
        } @else if (!upcomingLoaded) {
          <div class="skel-stack" aria-hidden="true">
            <span class="bk-skel" style="width: 62%; height: 44px"></span>
            <span class="bk-skel" style="width: 44%; height: 16px"></span>
            <span class="bk-skel" style="width: 100%; height: 14px; margin-top: 12px"></span>
            <span class="bk-skel" style="width: 100%; height: 14px"></span>
          </div>
        } @else if (upcomingFailed) {
          <div class="empty" role="status">
            <p class="empty__title">Scheduled sends could not be loaded.</p>
            <p class="empty__body">Check your connection, then try again.</p>
            <button type="button" class="bk-btn bk-btn--ghost" (click)="loadUpcoming()">
              Try again
            </button>
          </div>
        } @else {
          <div class="empty">
            <p class="empty__title">Nothing scheduled.</p>
            <p class="empty__body">
              Schedule a message and it waits here, with a countdown, until it
              goes out.
            </p>
          </div>
        }

        <!-- Past-due schedules: not "due now", they did not go out -->
        @if (overdue.length) {
          <p class="next__overdue" role="status">
            <span class="next__overdue-dot" aria-hidden="true"></span>
            <span class="next__overdue-text">
              @if (overdue.length === 1) {
                <strong>{{ overdue[0].messageSubject || "A scheduled message" }}</strong>
                was due {{ whenOf(overdue[0]) }} and hasn’t gone out.
              } @else {
                <strong>{{ formatCount(overdue.length) }} scheduled sends</strong>
                are past due and haven’t gone out.
              }
            </span>
            @if (overdue.length === 1) {
              <button type="button" class="bk-more next__overdue-go" (click)="openMessage(overdue[0])">
                Review <span class="bk-sym" aria-hidden="true">arrow_outward</span>
              </button>
            } @else {
              <a class="bk-more next__overdue-go" routerLink="/organization/messages" [queryParams]="{ status: 'pending' }">
                Review <span class="bk-sym" aria-hidden="true">arrow_outward</span>
              </a>
            }
          </p>
        }
      </section>

      <!-- ── Campaigns ── -->
      <section class="sec" aria-labelledby="dash-campaigns">
        <header class="sec__head">
          <h2 class="sec__title" id="dash-campaigns">
            Campaigns
            @if (runningCount > 0) {
              <a class="sec__count sec__count--link" routerLink="/organization/campaigns" [queryParams]="{ status: 'running' }"
                >{{ formatCount(runningCount) }} running</a
              >
            }
          </h2>
          <a class="bk-more" routerLink="/organization/campaigns">
            All campaigns <span class="bk-sym" aria-hidden="true">arrow_outward</span>
          </a>
        </header>

        @if (dashboardCampaigns.length) {
          <ul class="camps">
            @for (campaign of dashboardCampaigns; track campaign.id) {
              <li>
                <button type="button" class="camp" (click)="openCampaign(campaign)">
                  <span class="camp__name">{{ campaign.name }}</span>
                  <app-status [text]="campaign.status ?? ''" [status]="campaign.status ?? ''" />
                  <span class="camp__when">{{ campaignWhen(campaign) }}</span>
                </button>
              </li>
            }
          </ul>
        } @else if (loading) {
          <div class="skel-rows" aria-hidden="true">
            @for (row of [1, 2, 3]; track row) {
              <span class="bk-skel" style="height: 14px"></span>
            }
          </div>
        } @else {
          <div class="empty">
            <p class="empty__title">No campaigns yet.</p>
            <p class="empty__body">Campaigns group messages around one goal and audience.</p>
            <button type="button" class="bk-btn bk-btn--ghost" (click)="goToCreateCampaigns()">
              New campaign
            </button>
          </div>
        }
      </section>
    </div>
  </div>

  <!-- ── Messages sent: any period, by hour, day, week or month ── -->
  <dash-sent-overview class="bk-panel dash__history" />
</div>
